import "bootstrap/dist/css/bootstrap.min.css";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createAddress, fetchAddresses, updateAddress, deleteAddress, selectAddress } from '../store/addressSlice.js';
import { fetchLocationByPincode, validatePincode } from '../utils/pincodeService.js';

const AddressManager = ({ onAddressSelect, selectedAddressId }) => {
  const dispatch = useDispatch();
  const user = useSelector(s => s.auth.user);
  const addresses = useSelector(s => s.addresses.items);
  
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    houseNo: "",
    street: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    label: "Home"
  });
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchAddresses(user.uid));
    }
  }, [dispatch, user]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-fetch location when pincode is entered
    if (name === 'pincode' && value.length === 6 && validatePincode(value)) {
      setIsLoadingLocation(true);
      try {
        const locationData = await fetchLocationByPincode(value);
        if (locationData) {
          setFormData(prev => ({
            ...prev,
            city: locationData.city,
            state: locationData.state,
            area: locationData.area
          }));
          toast.success('Location details fetched successfully!');
        } else {
          toast.warning('Could not fetch location details for this pincode');
        }
      } catch (error) {
        toast.error('Failed to fetch location details');
      } finally {
        setIsLoadingLocation(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phone || !formData.houseNo || !formData.street || !formData.city || !formData.state || !formData.pincode) {
      toast.error("Please fill all required fields");
      return;
    }
    
    if (!validatePincode(formData.pincode)) {
      toast.error("Please enter a valid 6-digit pincode");
      return;
    }

    try {
      if (editingAddress) {
        await dispatch(updateAddress({
          userId: user.uid,
          addressId: editingAddress.id,
          address: formData
        })).unwrap();
        toast.success("Address updated successfully");
      } else {
        await dispatch(createAddress({
          userId: user.uid,
          address: formData
        })).unwrap();
        toast.success("Address added successfully");
      }
      
      resetForm();
    } catch (error) {
      toast.error("Failed to save address");
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      phone: "",
      houseNo: "",
      street: "",
      area: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      label: "Home"
    });
    setShowForm(false);
    setEditingAddress(null);
    setIsLoadingLocation(false);
  };

  const handleEdit = (address) => {
    setFormData(address);
    setEditingAddress(address);
    setShowForm(true);
  };

  const handleDelete = async (addressId) => {
    if (window.confirm("Are you sure you want to delete this address?")) {
      try {
        await dispatch(deleteAddress({ userId: user.uid, addressId })).unwrap();
        toast.success("Address deleted successfully");
      } catch (error) {
        toast.error("Failed to delete address");
      }
    }
  };

  const handleSelectAddress = (address) => {
    dispatch(selectAddress(address));
    if (onAddressSelect) {
      onAddressSelect(address);
    }
  };

  return (
    <div className="address-manager">
      <style>{`
        .address-card {
          transition: all 0.3s ease;
          cursor: pointer;
          border: 2px solid transparent;
        }
        .address-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .address-card.selected {
          border-color: #2f0f0c;
          background: linear-gradient(135deg, #2f0f0c05, #2f0f0c10);
        }
        .form-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .address-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }
        .add-address-btn {
          background: linear-gradient(135deg, #2f0f0c, #4a1a15);
          border: none;
          transition: all 0.3s ease;
        }
        .add-address-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(47, 15, 12, 0.3);
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-bold mb-0">
          <i className="bi bi-geo-alt me-2 text-primary"></i>
          Delivery Addresses
        </h5>
        <button
          className="btn add-address-btn text-white rounded-pill px-4"
          onClick={() => setShowForm(!showForm)}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add New Address
        </button>
      </div>

      {showForm && (
        <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 form-slide-in">
          <h6 className="fw-bold mb-3">
            <i className="bi bi-house-add me-2"></i>
            {editingAddress ? "Edit Address" : "Add New Address"}
          </h6>
          
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Full Name *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Phone Number *</label>
                <input
                  type="tel"
                  className="form-control rounded-3"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 9876543210"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">House/Flat No. *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="houseNo"
                  value={formData.houseNo}
                  onChange={handleInputChange}
                  placeholder="House/Flat/Building No."
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Street/Road *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  placeholder="Street/Road Name"
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Area/Locality</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  placeholder="Area/Locality"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Landmark</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  placeholder="Nearby landmark (optional)"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">City *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-semibold">State *</label>
                <input
                  type="text"
                  className="form-control rounded-3"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="State"
                  required
                />
              </div>
                <div className="col-md-4">
                <label className="form-label small fw-semibold">PIN Code *</label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control rounded-start-3"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="123456"
                    pattern="[0-9]{6}"
                    required
                  />
                  {isLoadingLocation && (
                    <span className="input-group-text rounded-end-3 bg-primary text-white">
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </span>
                  )}
                </div>
                {formData.pincode.length === 6 && !isLoadingLocation && (
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Location auto-filled from pincode
                  </small>
                )}
              </div>
              <div className="col-md-6">
                <label className="form-label small fw-semibold">Address Label</label>
                <select
                  className="form-select rounded-3"
                  name="label"
                  value={formData.label}
                  onChange={handleInputChange}
                >
                  <option value="Home">🏠 Home</option>
                  <option value="Office">🏢 Office</option>
                  <option value="Other">📍 Other</option>
                </select>
              </div>
            </div>
            
            <div className="row g-3 mt-2">
              <div className="col-12">
                <label className="form-label small fw-semibold">Complete Address *</label>
                <textarea
                  className="form-control rounded-3"
                  name="completeAddress"
                  value={`${formData.houseNo}${formData.houseNo ? ', ' : ''}${formData.street}${formData.street ? ', ' : ''}${formData.area}${formData.area ? ', ' : ''}${formData.landmark ? 'Near ' + formData.landmark + ', ' : ''}${formData.city}, ${formData.state} - ${formData.pincode}`}
                  placeholder="Complete address will be auto-generated"
                  rows="3"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                <small className="text-muted">This address is automatically generated from the fields above</small>
              </div>
            </div>
            
            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-dark rounded-pill px-4">
                <i className="bi bi-check-circle me-2"></i>
                {editingAddress ? "Update Address" : "Save Address"}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {addresses.length > 0 ? (
        <div className="address-grid">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`card address-card rounded-4 p-4 ${
                selectedAddressId === address.id ? "selected" : ""
              }`}
              onClick={() => handleSelectAddress(address)}
            >
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center">
                  <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 me-2">
                    {address.label === "Home" ? "🏠" : address.label === "Office" ? "🏢" : "📍"} {address.label}
                  </span>
                  {selectedAddressId === address.id && (
                    <span className="badge bg-success rounded-pill">
                      <i className="bi bi-check-circle me-1"></i>Selected
                    </span>
                  )}
                </div>
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-outline-secondary rounded-circle"
                    data-bs-toggle="dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(address);
                        }}
                      >
                        <i className="bi bi-pencil me-2"></i>Edit
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item text-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(address.id);
                        }}
                      >
                        <i className="bi bi-trash me-2"></i>Delete
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mb-2">
                <strong className="text-dark">{address.fullName}</strong>
                <div className="text-muted small mt-1">
                  <i className="bi bi-telephone me-1"></i>{address.phone}
                </div>
              </div>
              
              <div className="text-muted small">
                <i className="bi bi-geo-alt me-1"></i>
                {address.houseNo}, {address.street}
                {address.area && `, ${address.area}`}
                {address.landmark && `, Near ${address.landmark}`}
                <br />
                {address.city}, {address.state} - {address.pincode}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-5">
          <div className="text-muted">
            <i className="bi bi-house-add display-4 d-block mb-3"></i>
            <h5>No addresses saved</h5>
            <p>Add your first delivery address to get started</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressManager;